import React, { useRef } from 'react';
import { Button, Modal, Col, Row } from 'antd';
import { FieldArray, Formik } from 'formik';
import { Form } from 'formik-antd';

import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import {
  AssignmentTypeEnum,
  CustomerMspOrVmsOverviewDto,
  JobCategoryEnum,
  OvertimeEnum,
} from 'api/backofficeApiClient';
import SubmitButton from 'components/formik/SubmitButton';
import { SimplePriceAgreementW2Info } from '../AssignmentDataModal/FinancialInfo';
import {
  MULTI_RATES_KEY,
  MultiRatesTypeEnum,
} from 'hooks/useFinancialInfoTabs';
import FinancialRatesSectionArray from '../AssignmentDataModal/FinancialInfo/FinancialRatesSectionArray';
import FinancialInfoScheduleSection from './FinancialInfoScheduleSection';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import Checkbox from 'components/formik/Checkbox';
import { getValidationSchema } from './validation';
import { AssignmentMultiRateViewModel } from 'api/adminApiClient';
import FinancialSchedulerContext from 'components/FinancialSchedulerContext';

import styles from './FinancialInfoScheduleModal.module.less';

export enum EffectiveDateMode {
  ADD = 1,
  EDIT = 2,
}

export interface FinancialInfoSchedulerModel {
  payRate?: number;
  billRate?: number;
  vmsFee?: number;
  mspFee?: number;
  vmsFeeIsOverridden?: boolean;
  mspFeeIsOverridden?: boolean;
  dtBillRate?: number;
  overtimeBillRate?: number;
  overtimePayRate?: number;
  profitRate?: number;
  overtime?: OvertimeEnum;
  mbpRate?: number;
}

export interface PayCodeInfoFormScheduleValues {
  payCodeId?: string;
  billRate?: number;
  payRate?: number;
  specialInstructions?: string;
  memberBilledIndicator?: boolean;
  hasPayRateCustomLogic?: boolean;
  hasBillRateCustomLogic?: boolean;
}

export interface MultiRateFormScheduleValues {
  payTypeId?: string;
  activePayCodeId?: string;
  payCodesInfo?: PayCodeInfoFormScheduleValues[];
}

export interface FinancialSchedulerFormValues {
  isCanUpdateScheduler?: boolean;
  effectiveDate?: Date;
  financialInfoScheduler?: FinancialInfoSchedulerModel;
  multiRates?: MultiRateFormScheduleValues[];
}

type Props = {
  mode: EffectiveDateMode;
  onClose: () => void;
  initialValues?: FinancialSchedulerFormValues;
  onSuccess: (data: FinancialSchedulerFormValues) => Promise<void> | void;
  mspOrVmsOverviewInfo?: CustomerMspOrVmsOverviewDto;
  onRemoveFinancialScheduler?: () => Promise<void> | void;
  externalRef?: any;
  w2InfoByWorkSiteWorkLocation?: SimplePriceAgreementW2Info;
  workStateLocation?: number;
  isW2Assignment: boolean;
  assignmentType: AssignmentTypeEnum | null;
  ptoMultiRate?: AssignmentMultiRateViewModel;
  jobCategoryValue?: JobCategoryEnum | null;
};

export default function FinancialInfoScheduleModal(props: Props) {
  const {
    onClose,
    initialValues: preInitialValues,
    onSuccess,
    mode,
    mspOrVmsOverviewInfo,
    onRemoveFinancialScheduler,
    w2InfoByWorkSiteWorkLocation,
    workStateLocation,
    isW2Assignment,
    assignmentType,
    ptoMultiRate,
    jobCategoryValue,
  } = props;
  const isUpdateMode = mode === EffectiveDateMode.EDIT;
  const formRef: any = useRef();

  const { externalRef = formRef } = props;

  const getInitialValues = (): FinancialSchedulerFormValues => {
    if (preInitialValues) {
      return { ...preInitialValues, isCanUpdateScheduler: !isUpdateMode };
    }

    return {
      isCanUpdateScheduler: !isUpdateMode,
      financialInfoScheduler: undefined,
      multiRates: [],
      effectiveDate: undefined,
    };
  };

  const initialValues = getInitialValues();

  const handleRemoveSchedule = () => {
    return Modal.confirm({
      title: 'Scheduled financial rates update will be removed. Are you sure?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        onRemoveFinancialScheduler && (await onRemoveFinancialScheduler());
      },
      onCancel() {},
    });
  };

  const handleCloseModal = () => {
    if (externalRef.current.isSubmitting) return;

    if (externalRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }

    onClose();
  };

  const getCorrectTitle = () => {
    switch (mode) {
      case EffectiveDateMode.ADD:
        return 'Warning! Financial rates were updated which might affect your billing. What is the effective date for this change?';
      case EffectiveDateMode.EDIT:
        return 'The following fields are scheduled for changes:';
    }
  };

  const handleSubmit = async (data: FinancialSchedulerFormValues) => {
    await onSuccess(data);
    onClose();
  };

  return (
    <FinancialSchedulerContext.Provider
      value={{
        jobCategory: jobCategoryValue,
        workSiteWorkLocation: null,
      }}
    >
      <BaseModal
        open
        title="Update financial information?"
        maxWidth={638}
        onCancel={handleCloseModal}
      >
        <Formik
          enableReinitialize
          innerRef={externalRef}
          validationSchema={getValidationSchema(initialValues)}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <Form layout="vertical">
              <div className="modal-scroll-wrapper">
                <span className="modal-title">{getCorrectTitle()}</span>
                {isUpdateMode && (
                  <div className={styles.checkboxWrap}>
                    <FormItem name="isCanUpdateScheduler">
                      <Checkbox name="isCanUpdateScheduler">
                        Update the scheduled changes
                      </Checkbox>
                    </FormItem>
                  </div>
                )}
                <Row>
                  <Col sm={24} md={12}>
                    <FormItem label="Effective date *" name="effectiveDate">
                      <DatePicker
                        name="effectiveDate"
                        disabled={!formik.values.isCanUpdateScheduler}
                        placeholder="Select end date"
                        showToday={false}
                        format="MM/DD/YYYY"
                        className="d-flex"
                      />
                    </FormItem>
                  </Col>
                </Row>

                <FieldArray
                  name={MULTI_RATES_KEY}
                  render={(props) => (
                    <FinancialRatesSectionArray
                      {...props}
                      mode={MultiRatesTypeEnum.Schedule}
                      selectedAssignmentType={assignmentType}
                      renderFinancialInfoSection={
                        <FinancialInfoScheduleSection
                          formik={formik}
                          initialValues={initialValues}
                          mspOrVmsOverviewInfo={mspOrVmsOverviewInfo}
                          w2InfoByWorkSiteWorkLocation={
                            w2InfoByWorkSiteWorkLocation
                          }
                          workStateLocation={workStateLocation}
                          isW2Assignment={isW2Assignment}
                          formRef={externalRef}
                          ptoMultiRate={ptoMultiRate}
                        />
                      }
                      jobCategory={jobCategoryValue}
                    />
                  )}
                />
              </div>
              <BaseModalFooter>
                <Button onClick={handleCloseModal}>Cancel</Button>
                {isUpdateMode && (
                  <Button type="primary" onClick={handleRemoveSchedule}>
                    Remove update
                  </Button>
                )}
                <SubmitButton type="primary">Update</SubmitButton>
              </BaseModalFooter>
            </Form>
          )}
        </Formik>
      </BaseModal>
    </FinancialSchedulerContext.Provider>
  );
}
